import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Star, Globe, Phone, Grid, ArrowRight, X, Zap, Folder } from 'react-feather';
import { servicesAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import AdsDisplay from '../components/AdsDisplay';
import { formatCurrencyWithConversion } from '../utils/currency';
import PlatformIcon, { detectPlatform } from '../components/PlatformIcon';
import './Services.css';

const FAVORITES_KEY = 'nb_favorite_services';

const PLATFORMS = [
  { id: 'all', name: 'All Platforms', iconType: 'grid' },
  { id: 'instagram', name: 'Instagram', platform: 'instagram' },
  { id: 'tiktok', name: 'TikTok', platform: 'tiktok' },
  { id: 'youtube', name: 'YouTube', platform: 'youtube' },
  { id: 'facebook', name: 'Facebook', platform: 'facebook' },
  { id: 'telegram', name: 'Telegram', platform: 'telegram' },
  { id: 'whatsapp', name: 'WhatsApp', platform: 'whatsapp' },
  { id: 'twitter', name: 'Twitter / X', platform: 'twitter' },
  { id: 'spotify', name: 'Spotify', platform: 'spotify' },
  { id: 'snapchat', name: 'Snapchat', platform: 'snapchat' },
  { id: 'linkedin', name: 'LinkedIn', platform: 'linkedin' },
  { id: 'discord', name: 'Discord', platform: 'discord' },
];

const SUBCATEGORIES = ['All', 'Followers', 'Likes', 'Views', 'Comments'];

export const detectSubCategory = (text = '') => {
  const t = (text || '').toLowerCase();
  if (t.includes('follower') || t.includes('subscriber') || t.includes('member')) return 'Followers';
  if (t.includes('like') || t.includes('heart') || t.includes('reaction')) return 'Likes';
  if (t.includes('view') || t.includes('watch') || t.includes('impression') || t.includes('stream')) return 'Views';
  if (t.includes('comment')) return 'Comments';
  return 'Other';
};

export function getServiceCategory(service, fallbackCategory = '') {
  if (service?.category && String(service.category).trim()) {
    return String(service.category).trim();
  }
  if (fallbackCategory && String(fallbackCategory).trim()) {
    return String(fallbackCategory).trim();
  }
  const name = service?.name || '';
  const plat = detectPlatform(name);
  if (plat && plat !== 'generic') {
    const formattedPlat = plat.charAt(0).toUpperCase() + plat.slice(1);
    const subCat = detectSubCategory(name);
    return `${formattedPlat} ${subCat !== 'Other' ? subCat : 'Services'}`;
  }
  return 'General Services';
}

export default function Services() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, publicSettings } = useAuth();
  const { t } = useLanguage();
  const userCurrency = user?.currency || 'USD';

  // Check virtual number accessibility
  const vnStatus = publicSettings?.virtual_numbers_status || 'beta';
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canAccessVirtualNumbers =
    isAdmin ||
    vnStatus === 'active' ||
    ((vnStatus === 'beta' || vnStatus === 'specific_users') && (user?.is_tester === 1 || user?.is_tester === true));

  const [servicesMap, setServicesMap] = useState({});
  const [flatServices, setFlatServices] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState(() => {
    const p = searchParams.get('platform');
    return p ? p.toLowerCase() : 'all';
  });
  const [selectedSubCategory, setSelectedSubCategory] = useState('All');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [viewLayout, setViewLayout] = useState('grid'); // 'grid' | 'grouped'
  const [displayLimit, setDisplayLimit] = useState(36);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sync platform if URL search param changes
  useEffect(() => {
    const p = searchParams.get('platform');
    if (p && p.toLowerCase() !== selectedPlatform) {
      setSelectedPlatform(p.toLowerCase());
    }
  }, [searchParams]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
      setFavorites(Array.isArray(saved) ? saved : []);
    } catch {
      setFavorites([]);
    }
  }, []);

  useEffect(() => {
    servicesAPI.getAll()
      .then((response) => {
        setServicesMap(response.data?.services || {});
        setFlatServices(response.data?.flat || []);
      })
      .catch(() => setError('Failed to load services.'))
      .finally(() => setLoading(false));
  }, []);

  // Reset pagination limit on filter change
  useEffect(() => {
    setDisplayLimit(36);
  }, [selectedPlatform, selectedSubCategory, search, showFavoritesOnly]);

  const favoriteSet = useMemo(() => new Set(favorites), [favorites]);

  const toggleFavorite = (serviceId) => {
    setFavorites((prev) => {
      const next = prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId];
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
      return next;
    });
  };

  // Filtered flat services list
  const filteredServices = useMemo(() => {
    return flatServices.filter((service) => {
      const fullText = `${service.category || ''} ${service.name || ''} #${service.id}`;
      const platform = detectPlatform(fullText);
      const subCat = detectSubCategory(fullText);

      // Platform match
      if (selectedPlatform !== 'all' && platform !== selectedPlatform) {
        return false;
      }

      // Subcategory match
      if (selectedSubCategory !== 'All' && subCat !== selectedSubCategory) {
        return false;
      }

      // Favorites filter
      if (showFavoritesOnly && !favoriteSet.has(service.id)) {
        return false;
      }

      // Search match
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const matchesName = (service.name || '').toLowerCase().includes(q);
        const matchesCat = (service.category || '').toLowerCase().includes(q);
        const matchesId = String(service.id).includes(q);
        if (!matchesName && !matchesCat && !matchesId) return false;
      }

      return true;
    });
  }, [flatServices, selectedPlatform, selectedSubCategory, showFavoritesOnly, favoriteSet, search]);

  // Grouped by Category for grouped view
  const groupedCategories = useMemo(() => {
    const groups = {};
    filteredServices.forEach((service) => {
      const cat = service.category || 'General';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(service);
    });
    return Object.entries(groups).map(([category, svcs]) => ({
      category,
      services: svcs,
      platform: detectPlatform(category),
    }));
  }, [filteredServices]);

  // Only display platforms that actually have active services in the current catalog
  const availablePlatforms = useMemo(() => {
    if (!flatServices || flatServices.length === 0) return PLATFORMS;

    const presentPlatforms = new Set();
    flatServices.forEach((service) => {
      const fullText = `${service.category || ''} ${service.name || ''} #${service.id}`;
      const plat = detectPlatform(fullText);
      if (plat && plat !== 'generic') {
        presentPlatforms.add(plat);
      }
    });

    return PLATFORMS.filter((item) => {
      if (item.id === 'all') return true;
      return presentPlatforms.has(item.id);
    });
  }, [flatServices]);

  // If active platform was selected but is no longer available (e.g. provider disabled), fallback to 'all'
  useEffect(() => {
    if (flatServices.length > 0 && selectedPlatform !== 'all') {
      const isAvailable = availablePlatforms.some((p) => p.id === selectedPlatform);
      if (!isAvailable) {
        setSelectedPlatform('all');
      }
    }
  }, [availablePlatforms, flatServices.length, selectedPlatform]);

  const handlePlatformSelect = (platId) => {
    setSelectedPlatform(platId);
    if (platId === 'all') {
      searchParams.delete('platform');
      setSearchParams(searchParams);
    } else {
      setSearchParams({ platform: platId });
    }
  };

  return (
    <div className="services-page">
      {/* ── 1. Top Category Switcher (Matching Mobile) ── */}
      <div className="services-top-tabs">
        <button
          type="button"
          className="services-tab-btn active"
        >
          <Globe size={15} />
          <span>{t('services.social_media_tab', 'Social Media')}</span>
        </button>

        {canAccessVirtualNumbers && (
          <button
            type="button"
            className="services-tab-btn"
            onClick={() => navigate('/dashboard/virtual-numbers')}
          >
            <Phone size={15} />
            <span>{t('services.virtual_numbers_tab', 'Virtual Numbers')}</span>
          </button>
        )}
      </div>

      {/* ── 2. Branded Promo / Hero Card ── */}
      <div className="services-promo-hero">
        <div className="services-promo-content">
          <h2 className="services-promo-title">
            {t('services.promo_title', 'Grow Your Social Media Faster')}
          </h2>
          <p className="services-promo-subtitle">
            {t('services.promo_subtitle', 'Get followers, likes, views, and engagements at verified lowest rates with automated delivery.')}
          </p>
          <div className="services-promo-actions">
            <Link to="/dashboard/orders/new" className="btn btn-primary services-promo-btn">
              {t('services.quick_order', 'Quick Order')} <ArrowRight size={15} />
            </Link>
            {selectedPlatform !== 'all' && (
              <button
                type="button"
                className="btn services-promo-reset-btn"
                onClick={() => handlePlatformSelect('all')}
              >
                {t('services.show_all_platforms', 'Show All Platforms')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── 3. Search & Quick Filters ── */}
      <div className="services-filter-box card">
        {/* Search Bar */}
        <div className="services-search-bar">
          <Search size={18} className="services-search-icon" />
          <input
            className="services-search-input"
            placeholder={t('services.search_placeholder', 'Search by service name, category, or ID (e.g. 1024, TikTok, Instagram followers)...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="services-search-clear"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Horizontal Platform Chips (Matching Mobile) */}
        <div className="services-platform-row">
          {availablePlatforms.map((item) => {
            const isActive = selectedPlatform === item.id;
            return (
              <button
                key={item.id}
                type="button"
                className={`services-platform-chip ${isActive ? 'active' : ''}`}
                onClick={() => handlePlatformSelect(item.id)}
              >
                {item.iconType === 'grid' ? (
                  <div className="services-chip-icon-grid">
                    <Grid size={15} />
                  </div>
                ) : (
                  <PlatformIcon platform={item.platform} size={24} iconSize={13} />
                )}
                <span>{item.name}</span>
              </button>
            );
          })}
        </div>

        {/* Subcategory Pills + Favorites */}
        <div className="services-subcat-row">
          <span className="services-subcat-label">Filter:</span>
          {SUBCATEGORIES.map((sub) => {
            const isActive = selectedSubCategory === sub;
            return (
              <button
                key={sub}
                type="button"
                className={`services-subcat-pill ${isActive ? 'active' : ''}`}
                onClick={() => setSelectedSubCategory(sub)}
              >
                {sub}
              </button>
            );
          })}

          <button
            type="button"
            className={`services-subcat-pill services-fav-pill ${showFavoritesOnly ? 'active' : ''}`}
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
          >
            <Star size={13} fill={showFavoritesOnly ? 'currentColor' : 'none'} />
            <span>{t('services.favorites', 'Favorites')} {favorites.length > 0 ? `(${favorites.length})` : ''}</span>
          </button>
        </div>
      </div>

      {/* ── 4. Ads Display ── */}
      <AdsDisplay adSlot="banner-300x250" className="medium-rectangle" />
      <AdsDisplay adSlot="banner-320x50" className="banner" />

      {/* ── 5. Services Header & Results Count ── */}
      <div className="services-results-header">
        <div>
          <h3 className="services-results-title">
            {selectedPlatform === 'all'
              ? 'All Social Media Services'
              : `${selectedPlatform.charAt(0).toUpperCase() + selectedPlatform.slice(1)} Services`}
            {selectedSubCategory !== 'All' && ` — ${selectedSubCategory}`}
          </h3>
          <p className="services-results-meta">
            Showing <strong>{Math.min(filteredServices.length, displayLimit)}</strong> of{' '}
            <strong>{filteredServices.length}</strong> available services
          </p>
        </div>

        <div className="services-view-switchers">
          <button
            type="button"
            className={`services-view-btn ${viewLayout === 'grid' ? 'active' : ''}`}
            onClick={() => setViewLayout('grid')}
            title="Card Grid View"
          >
            <Grid size={15} />
            <span className="view-btn-text">Grid</span>
          </button>
          <button
            type="button"
            className={`services-view-btn ${viewLayout === 'grouped' ? 'active' : ''}`}
            onClick={() => setViewLayout('grouped')}
            title="Grouped by Category View"
          >
            <Globe size={15} />
            <span className="view-btn-text">Categories</span>
          </button>
        </div>
      </div>

      {/* ── 6. Services Content ── */}
      {loading ? (
        <div className="card services-loading-card">
          <div className="spinner" />
          <p className="services-loading-text">Loading services catalog...</p>
        </div>
      ) : error ? (
        <div className="alert alert-error">{error}</div>
      ) : filteredServices.length === 0 ? (
        <div className="card services-empty-card">
          <div className="services-empty-icon">
            <Search size={32} />
          </div>
          <h4>{t('services.no_services_found', 'No services found')}</h4>
          <p>
            {search
              ? `No services matched "${search}". Try adjusting your keywords or clearing filters.`
              : 'No services available for this platform or subcategory.'}
          </p>
          {(search || selectedPlatform !== 'all' || selectedSubCategory !== 'All' || showFavoritesOnly) && (
            <button
              type="button"
              className="btn btn-outline-hero"
              style={{ marginTop: 14 }}
              onClick={() => {
                setSearch('');
                handlePlatformSelect('all');
                setSelectedSubCategory('All');
                setShowFavoritesOnly(false);
              }}
            >
              Reset Filters
            </button>
          )}
        </div>
      ) : viewLayout === 'grouped' ? (
        /* ── Grouped View ── */
        <div className="services-category-grid">
          {groupedCategories.map(({ category, services, platform }) => (
            <div key={category} className="card services-category-card">
              <div className="services-category-header">
                <PlatformIcon platform={platform} size={40} iconSize={20} />
                <div className="services-category-title-wrap">
                  <div className="services-section-title">{category}</div>
                  <div className="services-count">{services.length} services</div>
                </div>
              </div>

              <div className="services-list">
                {services.map((service) => {
                  const isFavorite = favoriteSet.has(service.id);
                  const price = parseFloat(service.final_price || service.price_per_1000 || 0);
                  const categoryName = getServiceCategory(service, category);

                  return (
                    <div key={service.id} className="services-item">
                      <div className="services-item-top">
                        <div className="services-item-top-left">
                          <span className="services-id-badge">#{service.id}</span>
                          <span className="services-item-cat-pill" title={categoryName}>
                            <Folder size={11} className="services-cat-icon" />
                            <span>{categoryName}</span>
                          </span>
                        </div>
                        <button
                          type="button"
                          className={`services-star-btn ${isFavorite ? 'active' : ''}`}
                          onClick={() => toggleFavorite(service.id)}
                          aria-label={isFavorite ? 'Remove favorite' : 'Add favorite'}
                          title={isFavorite ? 'Remove favorite' : 'Add favorite'}
                        >
                          <Star size={14} fill={isFavorite ? 'currentColor' : 'none'} />
                        </button>
                      </div>

                      <div className="services-name">{service.name}</div>

                      <div className="services-meta-tags">
                        <div className="services-meta-rate">
                          <span className="services-meta-label">Rate / 1K</span>
                          <span className="services-rate-value">
                            {formatCurrencyWithConversion(price, userCurrency, 3)}
                          </span>
                        </div>
                        <div className="services-meta-range">
                          <span className="services-meta-label">Min / Max</span>
                          <span className="services-range-value">
                            {service.min?.toLocaleString()} / {service.max?.toLocaleString()}
                          </span>
                        </div>
                      </div>

                      <div className="services-item-actions">
                        <Link
                          to={`/dashboard/orders/new?category=${encodeURIComponent(categoryName)}&service_id=${service.id}`}
                          className="services-order-btn"
                        >
                          Order Now <ArrowRight size={13} />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Grid View (Matching Mobile ServiceCard UX) ── */
        <>
          <div className="services-card-grid">
            {filteredServices.slice(0, displayLimit).map((service) => {
              const categoryName = getServiceCategory(service);
              const fullText = `${categoryName} ${service.name || ''}`;
              const platform = detectPlatform(fullText);
              const isFavorite = favoriteSet.has(service.id);
              const price = parseFloat(service.final_price || service.price_per_1000 || 0);

              return (
                <div key={service.id} className="services-modern-card card">
                  <div className="services-card-header">
                    <div className="services-card-platform-wrap">
                      <PlatformIcon platform={platform} size={36} iconSize={18} />
                      <span className="services-id-badge">#{service.id}</span>
                    </div>

                    <button
                      type="button"
                      className={`services-star-btn ${isFavorite ? 'active' : ''}`}
                      onClick={() => toggleFavorite(service.id)}
                      aria-label={isFavorite ? 'Remove favorite' : 'Add favorite'}
                      title={isFavorite ? 'Remove favorite' : 'Add favorite'}
                    >
                      <Star size={15} fill={isFavorite ? 'currentColor' : 'none'} />
                    </button>
                  </div>

                  <h4 className="services-card-title" title={service.name}>
                    {service.name}
                  </h4>

                  {/* Guaranteed Visible Service Category Pill */}
                  <div className="services-card-category-pill" title={categoryName}>
                    <Folder size={12} className="services-cat-icon" />
                    <span>{categoryName}</span>
                  </div>

                  <div className="services-card-details">
                    <div className="services-card-detail-item">
                      <span className="services-card-detail-label">{t('common.rate_per_1k', 'Rate per 1000')}</span>
                      <span className="services-card-detail-price">
                        {formatCurrencyWithConversion(price, userCurrency, 3)}
                      </span>
                    </div>

                    <div className="services-card-detail-item text-right">
                      <span className="services-card-detail-label">{t('common.min_max', 'Min / Max')}</span>
                      <span className="services-card-detail-val">
                        {service.min?.toLocaleString()} / {service.max?.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="services-card-footer">
                    <Link
                      to={`/dashboard/orders/new?category=${encodeURIComponent(categoryName)}&service_id=${service.id}`}
                      className="services-card-order-btn"
                    >
                      <span>{t('services.buy_now', 'Order Now')}</span>
                      <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {displayLimit < filteredServices.length && (
            <div className="services-load-more-wrap">
              <button
                type="button"
                className="btn btn-outline-hero services-load-more-btn"
                onClick={() => setDisplayLimit((prev) => prev + 36)}
              >
                Load More Services ({filteredServices.length - displayLimit} remaining)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
